import React from "react";
import { redirect } from "react-router-dom";

import jwt from "jsonwebtoken";

import FormAlert from "../components/FormAlert";
import { Breakpoint } from "../hooks/useBreakpoint";
import { ILeague, ITeam } from "../types/api";
import { IAlertParams } from "../types/shared";
import { ILocalStorageUserData } from "../types/users";

import { store } from "..";

export const getTokenFromLocalStorage = (): string | null => {
    const userDataRaw = localStorage.getItem("userData");

    if (userDataRaw) {
        const userData: ILocalStorageUserData = JSON.parse(userDataRaw);
        return userData.token;
    } else {
        return null;
    }
};

export const getUserFromLocalStorage = ():
    | ILocalStorageUserData["user"]
    | null => {
    const userDataRaw = localStorage.getItem("userData");

    if (userDataRaw) {
        const userData: ILocalStorageUserData = JSON.parse(userDataRaw);
        return userData.user;
    } else {
        return null;
    }
};

export const validateToken = (token: string): boolean => {
    try {
        jwt.verify(token, process.env.JWT_SECRET as string);
        return true;
    } catch (error) {
        return false;
    }
};

const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
    return null;
};

export const isLoggedIn = (): boolean => {
    let isValid = false;
    const token = getTokenFromLocalStorage();

    if (token) {
        isValid = validateToken(token);
    }

    if (isValid) {
        return true;
    } else {
        const refreshToken = getCookie("majorybl_refresh_token");

        if (refreshToken) {
            isValid = validateToken(refreshToken);
        }
    }

    return isValid;
};

export const logout = () => {
    // TODO send call to tokenBlacklist API function
    localStorage.removeItem("userData");
    return redirect("/login");
};

export const displayFormAlert = (params: IAlertParams): React.JSX.Element => (
    <FormAlert {...params} />
);

export const canEditTeam = (team: ITeam): boolean => {
    const user: ILocalStorageUserData["user"] | null =
        store.getState().auth.user;

    if (!user) {
        return false;
    } else {
        if ([1, 2].includes(user?.userLevel)) {
            return true;
        }

        if (user?.userLevel > 5) {
            return false;
        }

        if (
            parseInt(user.id.toString()) ===
                parseInt(team.coach.id.toString()) ||
            (team.organization && user.id === team.organization.primaryUser)
        ) {
            return true;
        }
    }
    return false;
};

export const canViewTeam = (): boolean => {
    const user = store.getState().auth.user;
    const league = store.getState().clubhouse.league;

    if (!user) {
        return false;
    } else {
        if (user?.userLevel > 5) {
            return false;
        }

        if (
            league?.teams?.some(team => team.coach?.id === user.id) ||
            league?.teams?.some(
                team => team.organization?.primaryUser === user.id,
            )
        ) {
            return true;
        }
    }
    return false;
};

export const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: "instant",
    });
};

export const defaultDateTimeFormat = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
});

export const defaultDateFormat = new Intl.DateTimeFormat("en-US", {
    dateStyle: "short",
});

export const defaultTimeFormat = new Intl.DateTimeFormat("en-US", {
    timeStyle: "short",
});

export const currencyFormat = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

export const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export const serializeForm = (formData: FormData) =>
    JSON.stringify(
        Array.from(formData.entries()).reduce(
            (m, [key, value]) => Object.assign(m, { [key]: value }),
            {},
        ),
    );

export const debounce = (callback: any, wait: number) => {
    let timeoutId: number | undefined = undefined;
    return (...args: any) => {
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
            callback.apply(null, args);
        }, wait);
    };
};

export const isXOrBigger = (x: Breakpoint, breakpoint: Breakpoint): boolean => {
    const breakpointsOrder: Record<Breakpoint, number> = {
        xs: 0,
        sm: 1,
        md: 2,
        lg: 3,
        xl: 4,
        xxl: 5,
    };

    return breakpointsOrder[breakpoint] >= breakpointsOrder[x];
};

export const isXOrSmaller = (
    x: Breakpoint,
    breakpoint: Breakpoint,
): boolean => {
    const breakpointsOrder: Record<Breakpoint, number> = {
        xs: 0,
        sm: 1,
        md: 2,
        lg: 3,
        xl: 4,
        xxl: 5,
    };

    return breakpointsOrder[breakpoint] <= breakpointsOrder[x];
};

export const isValidDate = (d: string) => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(d)) {
        return false;
    }

    const date = new Date(d);

    if (isNaN(date.getTime())) {
        return false;
    }

    return true;
};

export const processTinyData = (content: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");

    const tables = doc.querySelectorAll("table");

    tables.forEach(table => {
        table.classList.add("table", "table-responsive", "table-striped");

        const firstRow = table.querySelector("tr");
        if (firstRow) {
            const cells = firstRow.querySelectorAll("td");
            cells.forEach(cell => {
                const th = document.createElement("th");
                th.innerHTML = cell.innerHTML;

                for (const attr of cell.attributes) {
                    th.setAttribute(attr.name, attr.value);
                }

                cell.replaceWith(th);
            });
        }
    });

    return doc.body.innerHTML;
};

export const sortLeaguesByDirection = (a: ILeague, b: ILeague) => {
    const directionOrder = ["North", "South", "East", "West"];

    const dirA = a.direction;
    const dirB = b.direction;

    if (!dirA && !dirB) return 0;

    if (!dirA) return 1;
    if (!dirB) return -1;

    return directionOrder.indexOf(dirA) - directionOrder.indexOf(dirB);
};
